import React, { useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetContractorsQuery } from "store/api/contractorsApi"
import { useCreateVendorOrderNewMutation } from "store/api/vendorApi"
import { productTypeOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"
import OrderItemType from "types/enums/OrderItemType"
import { ReactComponent as IconInfo } from "assets/svg/icon-info-message.svg"
import { ReactComponent as IconMenuDown } from "assets/svg/icon-menu-down.svg"
import { ReactComponent as IconNewOrder } from "assets/svg/modal-icon-new-order.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { ButtonNew } from "components/form/ButtonNew"
import { SelectDynamic } from "components/form/SelectDynamic"
import { SelectStatic } from "components/form/SelectStatic"

import { OrderItemRow } from "./OrderItemRow"

import styles from "./index.module.scss"

interface Props {
  onCancel: () => void
  onSuccess: () => void
}

export interface OrderItem {
  id: string
  productType: "thermostat" | "cwa"
  quantity: number
  orderType: UITypes.Option[]
  warehouse: UITypes.Option[]
  status?: string
}

export const NewVendorOrderModal = ({ onCancel, onSuccess }: Props) => {
  const [triggerCreateVendorOrderNew, { isLoading }] = useCreateVendorOrderNewMutation()
  const [selectedContractor, setSelectedContractor] = useState<undefined | UITypes.Option[]>(undefined)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])

  const handleProductTypeSelect = (values: UITypes.Option[]) => {
    if (!values.length) return

    const productType = values[0].value as "thermostat" | "cwa"
    const defaultQuantity = productType === "thermostat" ? 20 : 1

    const newItem: OrderItem = {
      id: uuidv4(),
      productType,
      quantity: defaultQuantity,
      orderType: [],
      warehouse: [],
      status: "new",
    }
    setOrderItems([...orderItems, newItem])
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!selectedContractor) {
      toast.error("Please select contractor")
      return
    }

    if (orderItems.length === 0) {
      toast.error("Please add at least one order item")
      return
    }

    // Validate all items have order type selected
    const itemsWithoutOrderType = orderItems.filter(item => !item.orderType.length)
    if (itemsWithoutOrderType.length > 0) {
      toast.error("Please select order type for all items")
      return
    }

    // Validate all items have warehouse selected
    const invalidItems = orderItems.filter(item => !item.warehouse.length)
    if (invalidItems.length > 0) {
      toast.error("Please select warehouse for all items")
      return
    }

    try {
      const orderData = {
        contractor_id: Number(selectedContractor[0].value),
        items: orderItems.map(item => ({
          quantity: item.quantity,
          product_type: item.productType,
          item_type: item.orderType[0].value as OrderItemType,
          warehouse_id: Number(item.warehouse[0].value),
        })),
      }

      const res = await triggerCreateVendorOrderNew(orderData)
      const error = getErrorMessage(res?.error)

      if (res?.error) {
        toast.error(error)
      } else {
        toast.success("Order has been successfully added!")
        onSuccess()
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to create order")
    }
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="880px"
          onConfirm={onSubmit}
          onCancel={onCancel}
          isFetching={isLoading}
          confirmButtonText="Save"
        >
          <ModalHeader title="New order" subtitle="Please input the number of Nuve Thermostats you want to order.">
            <IconNewOrder />
          </ModalHeader>
          <div className={styles.modalContent}>
            <div className={styles.orderDetails}>
              <div className={styles.orderDetailsRow}>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Contractor ID</span>
                  <span className={styles.orderDetailsValue}>{selectedContractor && selectedContractor[0].value}</span>
                </div>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Creation date</span>
                  <span className={styles.orderDetailsValue}>{dayjs().format("L")}</span>
                </div>
              </div>
              <div className={styles.orderDetailsRow}>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Company Name</span>
                  <SelectDynamic
                    values={selectedContractor || []}
                    valueName="id"
                    labelName="name"
                    name="contractors"
                    useLazyQuery={useLazyGetContractorsQuery}
                    placeholder="Company name"
                    onChange={setSelectedContractor}
                    className=""
                    queryParams={{ isEnabled: 1 }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.separator} />
            <SelectStatic
              values={[]}
              options={productTypeOptions}
              className={styles.productTypeSelect}
              onChange={handleProductTypeSelect}
              contentRenderer={() => (
                <div className={styles.addNewButtonWrapper}>
                  <ButtonNew color="blue" className={styles.addNewButton}>
                    Add new <IconMenuDown className={styles.dropdownIndicator} />
                  </ButtonNew>
                </div>
              )}
              isSearchable={false}
              backspaceDelete={false}
            />
            {orderItems.map((item, index) => (
              <OrderItemRow
                key={item.id}
                item={item}
                index={index}
                orderItems={orderItems}
                setOrderItems={setOrderItems}
              />
            ))}
            <div className={styles.orderNote}>
              <IconInfo />
              Note: We can provide up to five (5) CWAs at no additional cost for every 20 thermostats ordered.
            </div>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
